import {
  Check,
  CheckCircle,
  LifeBuoy,
  Siren,
  Sparkles,
  UserPlus,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { Image, XStack, YStack } from "tamagui";
import { PlanTemplateType } from "../../templates/generated/template_types";
import { useStores } from "../core/stores";
import { trackEvent } from "../core/trackEvent";
import Blinking from "../ui/Blinking";
import { Body } from "../ui/Body";
import { Box } from "../ui/Box";
import { Button } from "../ui/Button";
import { Label } from "../ui/Label";
import { Title } from "../ui/Title";
import type PlanModel from "./PlanModel";
import { AccountSheetActive } from "./PlanModel";
import { usePlanNavigation } from "./usePlanNavigation";

export const CreatorTodoListGuide = observer(
  ({ plan }: { plan: PlanModel }) => {
    const { sessionStore } = useStores();

    const [isActive, setIsActive] = React.useState(false);

    const invitationsDone = plan.usersCount > 1;
    const registeredEmail = sessionStore.isRegistered;
    const { navigate } = usePlanNavigation();

    const completion = (invitationsDone ? 1 : 0) + (registeredEmail ? 1 : 0);

    const closeGuide = React.useCallback(() => {
      setIsActive(false);

      trackEvent("close_quickstart_guide");
    }, []);

    // Skip for custom templates as well
    if (
      invitationsDone ||
      completion === 2 ||
      plan.data.template === PlanTemplateType.CustomTemplate
    ) {
      // TODO - show a receipt here if creator?
      return null;
    }

    return (
      <>
        {!isActive && (
          <XStack
            gap="$medium"
            justifyContent="space-between"
            alignItems="center"
            width={"100%"}
            padding="$large"
            backgroundColor={"$backgroundMutedBlock"}
          >
            <XStack flex={1} gap="$medium" alignItems="center">
              <Blinking>
                <Siren color="$danger" size={"$medium"} />
              </Blinking>
              <YStack flex={1}>
                <Label>You are the creator</Label>
                <XStack gap="$medium">
                  <Body muted>TLDR: Add members and share your link</Body>
                </XStack>
              </YStack>
            </XStack>

            <Body
              flexShrink={0}
              cursor="pointer"
              onPress={() => {
                setIsActive(!isActive);

                trackEvent("open_quickstart_guide");
              }}
            >
              Show guide
            </Body>
          </XStack>
        )}

        {isActive && (
          <YStack gap={1}>
            <Box position="relative" backgroundColor={"$backgroundMutedBlock"}>
              <XStack gap="$space.small" alignItems="center">
                <Siren color={"$danger"} />
                <Title>Your responsibility</Title>
              </XStack>
              <YStack
                gap={"$medium"}
                $xs={{
                  paddingRight: 134,
                }}
                paddingRight={163}
              >
                <Body>
                  As the creator, ensure members join and understand the
                  context. Be proactive and helpful for success.
                </Body>

                <Body
                  cursor="pointer"
                  textDecorationLine="underline"
                  onPress={closeGuide}
                  muted
                >
                  {isActive ? "Minimize" : "Show"}
                </Body>
                <Image
                  $xs={{
                    paddingRight: -40,
                  }}
                  right={-12}
                  position="absolute"
                  bottom={-29}
                  zIndex={1}
                  src={require("../../assets/illustrations/laptopgirl.png")}
                />
              </YStack>
            </Box>
            <Box>
              <XStack gap="$medium" alignItems="center" width={"100%"}>
                {invitationsDone ? (
                  <CheckCircle color="$success" />
                ) : (
                  <UserPlus />
                )}
                <Label
                  flex={1}
                  textDecorationLine={
                    invitationsDone ? "line-through" : undefined
                  }
                >
                  Invitations needed
                </Label>
              </XStack>
              <Body muted>
                Start collaborating and add some action by inviting your friends
                using the link found in the top menu
              </Body>
              <Body
                textDecorationLine="underline"
                cursor="pointer"
                onPress={() => {
                  plan.setShowShare(true);
                }}
              >
                Share now
              </Body>
            </Box>

            <Box>
              <XStack gap="$medium" alignItems="center" width={"100%"}>
                {registeredEmail ? (
                  <CheckCircle color="$success" />
                ) : (
                  <LifeBuoy />
                )}
                <Label
                  flex={1}
                  textDecorationLine={
                    registeredEmail ? "line-through" : undefined
                  }
                >
                  Help your group
                </Label>
              </XStack>
              <Body muted>
                Register your email to keep up to date with new activity and so
                you know when it's time for some action
              </Body>
              <Body
                textDecorationLine="underline"
                cursor="pointer"
                onPress={() =>
                  plan.setShowAccountSheet(AccountSheetActive.Register)
                }
              >
                Stay connected
              </Body>
            </Box>

            <Box>
              <XStack gap="$medium" alignItems="center" width={"100%"}>
                <Sparkles />
                <Label flex={1}>Make it yours</Label>
              </XStack>

              <Body muted>
                Set the context by setting the title, make sure your members
                know what's it all about
              </Body>
              <Body
                textDecorationLine="underline"
                cursor="pointer"
                onPress={() =>
                  navigate("Module", {
                    planUid: plan.uid,
                    moduleUid: plan.planInfo.uid,
                  })
                }
              >
                Edit information
              </Body>
            </Box>

            <Button onPress={closeGuide} type="secondary" icon={Check}>
              Minimize guide
            </Button>
          </YStack>
        )}
      </>
    );
  },
);
